// Plain types and constants shared by the server and web app for the Staff
// module. Roles and their rules live in the account platform
// (`docs/staff-roles.md` there); this console only proxies and displays them.

/** Roles the console may grant or revoke. `platform_admin` stays a `staff.js` command. */
export const consoleManagedRoles = [
  "stacks_operator",
  "support",
  "billing",
  "operations",
  "investor_relations"
] as const;
export type ConsoleManagedRole = (typeof consoleManagedRoles)[number];

/** Minutes a sign-in counts as fresh for role changes (the account platform decides). */
export const freshSignInMinutes = 10;

export type StaffRole = {
  role: string;
  displayName: string;
  permissions: string[];
  consoleManaged: boolean;
};

export type StaffMember = {
  userId: string;
  email: string;
  name: string;
  accountStatus: string;
  twoFactorEnabled: boolean;
  /**
   * The account's staff-grade sign-in (account-platform `secureSignInSql`): an authenticator app,
   * or a passkey with the password turned off. Absent before account-platform's release with
   * passkey staff sign-in (backlog #90); `twoFactorEnabled` then stands in for it.
   */
  secureSignIn?: "authenticator_app" | "passkey" | null;
  roles: { role: string; assignedAt: string; assignedBy: string | null }[];
};

export type StaffRoleChange = {
  id: string;
  action: "granted" | "revoked";
  role: string | null;
  source: string;
  actor: string | null;
  target: string | null;
  occurredAt: string;
};

export type StaffOverview =
  | {
      available: true;
      roles: StaffRole[];
      staff: StaffMember[];
      history: StaffRoleChange[];
      /** The signed-in staff member's account id. */
      you: string;
      /** When the current sign-in stops counting as fresh, or null if it already has. */
      freshUntil: string | null;
    }
  /** The account platform doesn't offer the role API yet (not released). */
  | { available: false };

export type StaffRoleChangeResult = {
  action: "grant" | "revoke";
  email: string;
  role: string;
  assigned?: boolean;
  revoked?: boolean;
};

/**
 * The member's staff-grade sign-in. Before account-platform's release with passkey staff sign-in
 * (backlog #90) `secureSignIn` is absent and only the authenticator app counted; `null` from the
 * new release means none.
 */
export function staffSignIn(member: StaffMember): "authenticator_app" | "passkey" | null {
  if (member.secureSignIn !== undefined) return member.secureSignIn;
  return member.twoFactorEnabled ? "authenticator_app" : null;
}
